import { useState } from 'react'
import AppDropdown from '../../../components/AppDropdown'
import AppPagination from '../../../components/AppPagination'
import SharedFilters from '../../../components/SharedFilters'
import ApprovalEmptyState from '../components/ApprovalEmptyState'
import { useLeaveApprovalScreen } from './hooks/useLeaveApprovalScreen'

function statusChipClass(status) {
  if (status === 'APPROVED') return 'bg-emerald-100 text-emerald-700'
  if (status === 'REJECTED') return 'bg-error-container text-error'
  return 'bg-primary-fixed text-primary'
}

export default function LeaveApprovalScreen() {
  const { state, actions } = useLeaveApprovalScreen()
  const [rejectModal, setRejectModal] = useState({ open: false, id: null, remark: '' })

  async function submitReject() {
    if (!rejectModal.id) return
    await actions.rejectLeave(rejectModal.id, rejectModal.remark || '')
    setRejectModal({ open: false, id: null, remark: '' })
  }

  const selectableCount = state.rows.filter((r) => r.approvalStatus === 'PENDING' && r.canApprove).length
  const isAllSelected = selectableCount > 0 && state.selectedIds.size >= selectableCount

  return (
    <div className="mx-auto w-full max-w-[1400px] pb-10">
      <header className="mb-8 rounded-[2rem] bg-surface-container-low px-8 py-8">
        <p className="text-xs font-bold uppercase tracking-[0.22em] text-primary">Approval Console</p>
        <h1 className="mt-2 text-4xl font-extrabold tracking-tight text-on-surface md:text-5xl">Leave Approval</h1>
        <p className="mt-4 max-w-3xl text-base leading-relaxed text-on-surface-variant md:text-lg">
          Review self-requested leave submissions and approve or reject them for your team.
        </p>
      </header>

      <div className="mb-6">
        <SharedFilters
          showSearch={true}
          searchField="User Name"
          searchText={state.searchText}
          setSearchText={actions.setSearchText}
          searchFieldOptions={[{ value: 'User Name', label: 'User Name' }]}
          showStatus={true}
          statusFilter={state.statusFilter}
          setStatusFilter={actions.setStatusFilter}
          statusOptions={[
            { value: 'PENDING', label: 'Pending' },
            { value: 'all', label: 'All Statuses' },
            { value: 'APPROVED', label: 'Approved' },
            { value: 'REJECTED', label: 'Rejected' },
          ]}
          visibleAccordionFilters={['agent', 'dateRange']}
          agentFilter={state.userFilter}
          setAgentFilter={actions.setUserFilter}
          agentOptions={state.userOptions.map((option) => ({
            value: String(option.id),
            label: option.name || `User #${option.id}`,
          }))}
          fromDate={state.fromDate}
          setFromDate={actions.setFromDate}
          toDate={state.toDate}
          setToDate={actions.setToDate}
          tools={[
            {
              key: 'multiSelect',
              label: state.isMultiSelectMode ? 'Cancel Selection' : 'Select Multiple',
              icon: state.isMultiSelectMode ? 'close' : 'checklist',
              iconClassName: state.isMultiSelectMode ? 'text-error' : 'text-primary',
            }
          ]}
          onToolSelect={(key) => {
            if (key === 'multiSelect') {
              actions.toggleMultiSelectMode();
            }
          }}
        />
      </div>

      {state.error ? (
        <div className="mb-4 rounded-xl border border-error/30 bg-error-container px-4 py-3 text-sm text-error">{state.error}</div>
      ) : null}
      {state.actionError ? (
        <div className="mb-4 rounded-xl border border-error/30 bg-error-container px-4 py-3 text-sm text-error">{state.actionError}</div>
      ) : null}
      {state.actionMessage ? (
        <div className="mb-4 rounded-xl border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{state.actionMessage}</div>
      ) : null}

      <section className="overflow-hidden rounded-3xl bg-surface-container-low relative">
        {state.isMultiSelectMode && (
          <div className="bg-surface-container-highest border-b border-outline-variant/30 px-6 py-4 flex items-center justify-between sticky top-0 z-10">
            <div className="flex items-center gap-4">
              <span className="text-sm font-bold text-on-surface">
                {state.selectedIds.size} selected
              </span>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={actions.bulkApprove}
                disabled={state.selectedIds.size === 0}
                className="rounded-full bg-emerald-100 hover:bg-emerald-200 px-6 py-2 text-xs font-bold uppercase tracking-wider text-emerald-700 disabled:opacity-50 transition"
              >
                Approve
              </button>
              <button
                type="button"
                onClick={actions.bulkReject}
                disabled={state.selectedIds.size === 0}
                className="rounded-full bg-error-container hover:bg-error-container/80 px-6 py-2 text-xs font-bold uppercase tracking-wider text-error disabled:opacity-50 transition"
              >
                Reject
              </button>
              <button
                type="button"
                onClick={actions.selectAll}
                disabled={selectableCount === 0}
                className="rounded-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 px-6 py-2 text-xs font-bold uppercase tracking-wider text-white transition shadow-xs"
              >
                {isAllSelected ? 'Deselect All' : 'Select All'}
              </button>
            </div>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1200px] border-separate border-spacing-0 text-left">
            <thead>
              <tr className="bg-surface-container-highest/40">
                <th className="px-6 py-5 text-xs font-bold uppercase tracking-[0.16em] text-on-surface-variant">User</th>
                <th className="px-6 py-5 text-xs font-bold uppercase tracking-[0.16em] text-on-surface-variant">Leave Date</th>
                <th className="px-6 py-5 text-xs font-bold uppercase tracking-[0.16em] text-on-surface-variant">Leave Type</th>
                <th className="px-6 py-5 text-xs font-bold uppercase tracking-[0.16em] text-on-surface-variant">Remark</th>
                <th className="px-6 py-5 text-xs font-bold uppercase tracking-[0.16em] text-on-surface-variant">Approval</th>
                <th className="px-6 py-5 text-xs font-bold uppercase tracking-[0.16em] text-on-surface-variant">Decision Remark</th>
                <th className="px-6 py-5 text-xs font-bold uppercase tracking-[0.16em] text-on-surface-variant">Requested At</th>
                <th className="px-6 py-5 text-xs font-bold uppercase tracking-[0.16em] text-on-surface-variant text-center">Actions</th>
                {state.isMultiSelectMode && (
                  <th className="px-6 py-5 text-xs font-bold uppercase tracking-[0.16em] text-on-surface-variant text-right">Select</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-outline-variant/20">
              {state.isLoading ? (
                <tr>
                  <td colSpan={8} className="px-6 py-16 text-center text-sm text-on-surface-variant">
                    Loading leave approval queue...
                  </td>
                </tr>
              ) : !state.error && state.rows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-16 text-center text-sm text-on-surface-variant">
                    <ApprovalEmptyState />
                  </td>
                </tr>
              ) : (
                state.rows.map((row) => {
                  const isPending = row.approvalStatus === 'PENDING'
                  const isBusy = state.activeActionId === row.id
                  return (
                    <tr key={row.id} className="transition-colors hover:bg-surface-container-lowest">
                      <td className="px-6 py-5 text-base font-semibold text-on-surface">{row.userName}</td>
                      <td className="px-6 py-5 text-base font-bold text-on-surface">{row.leaveDate}</td>
                      <td className="px-6 py-5 text-base text-on-surface">{row.leaveType}</td>
                      <td className="px-6 py-5 text-sm text-on-surface-variant">{row.remark}</td>
                      <td className="px-6 py-5">
                        <span className={`rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider ${statusChipClass(row.approvalStatus)}`}>
                          {row.approvalStatus}
                        </span>
                      </td>
                      <td className="px-6 py-5 text-sm text-on-surface-variant">{row.decisionRemark}</td>
                      <td className="px-6 py-5 text-sm text-on-surface-variant">{row.createdAt}</td>
                      <td className="px-6 py-5">
                        <div className="flex items-center justify-end gap-2">
                          {!state.isMultiSelectMode && row.canApprove && isPending ? (
                            <>
                              <button
                                type="button"
                                disabled={isBusy}
                                onClick={() => actions.approveLeave(row.id)}
                                className="rounded-full bg-emerald-100 px-4 py-2 text-xs font-bold uppercase tracking-wider text-emerald-700 disabled:opacity-50"
                              >
                                {isBusy ? 'Working...' : 'Approve'}
                              </button>
                              <button
                                type="button"
                                disabled={isBusy}
                                onClick={() => setRejectModal({ open: true, id: row.id, remark: '' })}
                                className="rounded-full bg-error-container px-4 py-2 text-xs font-bold uppercase tracking-wider text-error disabled:opacity-50"
                              >
                                {isBusy ? 'Working...' : 'Reject'}
                              </button>
                            </>
                          ) : (
                            <span />
                          )}
                        </div>
                      </td>
                      {state.isMultiSelectMode && (
                        <td className="px-6 py-5 text-right">
                          {row.canApprove && isPending ? (
                            <input
                              type="checkbox"
                              checked={state.selectedIds.has(row.id)}
                              onChange={() => actions.toggleSelection(row.id)}
                              className="h-5 w-5 rounded border-outline-variant text-primary focus:ring-primary cursor-pointer"
                            />
                          ) : (
                            <input
                              type="checkbox"
                              disabled
                              className="h-5 w-5 rounded border-outline-variant opacity-30 cursor-not-allowed"
                            />
                          )}
                        </td>
                      )}
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4 border-t border-outline-variant/20 px-6 py-5">
          <span className="text-sm text-on-surface-variant">
            Showing {state.rangeStart}-{state.rangeEnd} of {state.totalCount} leave requests
          </span>

          <AppPagination
            page={state.page}
            totalPages={state.totalPages}
            onPageChange={actions.goToPage}
            onPrevious={actions.goToPreviousPage}
            onNext={actions.goToNextPage}
            canPrevious={state.page > 1}
            canNext={Boolean(state.hasMore) || state.page < state.totalPages}
          />
        </div>
      </section>

      <section className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-3">
        <article className="rounded-2xl bg-surface-container-low p-6">
          <p className="text-xs font-bold uppercase tracking-widest text-on-surface-variant">Pending</p>
          <h3 className="mt-2 text-4xl font-black text-on-surface">{state.totals.pending}</h3>
        </article>
        <article className="rounded-2xl bg-surface-container-low p-6">
          <p className="text-xs font-bold uppercase tracking-widest text-on-surface-variant">Approved</p>
          <h3 className="mt-2 text-4xl font-black text-on-surface">{state.totals.approved}</h3>
        </article>
        <article className="rounded-2xl bg-surface-container-low p-6">
          <p className="text-xs font-bold uppercase tracking-widest text-on-surface-variant">Rejected</p>
          <h3 className="mt-2 text-4xl font-black text-on-surface">{state.totals.rejected}</h3>
        </article>
      </section>

      {rejectModal.open ? (
        <div className="fixed inset-0 z-[85] flex items-center justify-center bg-black/35 p-4">
          <div className="w-full max-w-lg rounded-2xl border border-outline-variant/30 bg-surface-container-lowest p-6 shadow-2xl">
            <h4 className="text-xl font-bold text-on-surface">Reject Leave Request</h4>
            <p className="mt-2 text-sm text-on-surface-variant">Add an optional decision remark for audit context.</p>
            <label className="mt-4 block">
              <span className="mb-2 block text-xs font-bold uppercase tracking-wider text-on-surface-variant">Decision Remark</span>
              <textarea
                rows={4}
                value={rejectModal.remark}
                onChange={(event) => setRejectModal((prev) => ({ ...prev, remark: event.target.value }))}
                className="w-full rounded-2xl border border-outline-variant/30 bg-surface-container-low px-4 py-3 text-sm outline-none"
                placeholder="Optional rejection note..."
              />
            </label>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setRejectModal({ open: false, id: null, remark: '' })}
                className="rounded-full border border-outline-variant/30 px-5 py-2 text-sm font-semibold text-on-surface"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitReject}
                className="rounded-full bg-error px-5 py-2 text-sm font-semibold text-white"
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
